<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';

type Props = {
    words: string[],
    reverse?: boolean,
    speed?: number
    state?: 'running' | 'paused'
}

const props = withDefaults(defineProps<Props>(), {
    reverse: false,
    speed: 30,
    state: 'running'
})

const width = ref<number>(0)
const marquee = ref<any>(null)
const state = ref<'running' | 'paused'>(props.state)

const words = computed(() => props.reverse ? [...[...props.words].reverse(), ...[...props.words].reverse()] : [...props.words, ...props.words])
const listWidth = computed(() => `${width.value * 2}px`)
const translateFrom = computed(() => `0`)
const translateTo = computed(() => `-${width.value}px`)
const direction = computed(() => props.reverse ? 'reverse' : 'normal')
const marqueeWidth = computed(() => marquee.value ? `${marquee.value.clientWidth}px` : 0)
const speed = computed(() => `${props.speed}s`)

const elementRef = (el: any, index: number) => {
    if (index === 0) {
        width.value = 0
    }
    if (index < words.value.length / 2) {
        nextTick(() => {
            width.value += el.clientWidth
        })
    }
}

const play = () => {
    state.value = 'running'
}

const pause = () => {
    state.value = 'paused'
}

defineExpose({ play, pause, state: () => state.value })
</script>
<style scoped lang="scss">
@import "@scss/mixins/inline-animation";

.marquee {
    width: v-bind(marqueeWidth);
    overflow: hidden;

    .list {
        min-width: auto;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        width: v-bind(listWidth);

        @include inline-animation(v-bind(speed), infinite, linear, 0, v-bind(direction)) {
            0% {
                transform: translateX(v-bind(translateFrom));
            }

            100% {
                transform: translateX(v-bind(translateTo))
            }
        }

        animation-play-state: v-bind(state);

        .item {
            width: auto;
            height: 100%;
            white-space: nowrap;
            user-select: none;
        }
    }
}
</style>
<template>
    <div :class="{ marquee: true, reverse }" ref="marquee">
        <div v-if="marqueeWidth !== 0" class="list">
            <template v-for="(text, i) in words" :key="i">
                <div class="item" :ref="(el: any) => elementRef(el, i)">
                    <slot name="default" :text="text" :index="i">{{ text }}</slot>
                </div>
            </template>
        </div>
    </div>
</template>